import { LAANC_GRID_LAYER } from './constants';
import type { AirspaceRestriction, DetailRow } from './types';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const formatAltitudeBand = (restriction: AirspaceRestriction): string | null => {
    const { altRef, lowerFt, upperFt } = restriction;

    if (lowerFt === null && upperFt === null) {
        return null;
    }

    const ref = altRef ?? '';
    const low = lowerFt === null ? '—' : lowerFt === 0 ? 'SFC' : `${lowerFt} ft`;
    const high = upperFt === null ? '—' : `${upperFt} ft`;
    const band = `${low} – ${high}`;

    return ref.length > 0 ? `${band} ${ref}` : band;
};

const formatDate = (iso: string): string =>
    new Date(iso).toLocaleString(undefined, {
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        month: 'short',
        timeZoneName: 'short'
    });

const formatEffective = (restriction: AirspaceRestriction): string | null => {
    const { effectiveEnd, effectiveStart } = restriction;

    if (effectiveStart === null && effectiveEnd === null) {
        return null;
    }

    const start = effectiveStart === null ? 'Active' : formatDate(effectiveStart);
    const end = effectiveEnd === null ? 'ongoing' : formatDate(effectiveEnd);

    return `${start} → ${end}`;
};

const formatRelativeTime = (iso: string | null): string | null => {
    if (iso === null) {
        return null;
    }

    const ms = Date.now() - new Date(iso).getTime();

    if (ms < 0) {
        return null;
    }

    if (ms < HOUR_MS) {
        return `${Math.max(1, Math.round(ms / MINUTE_MS))}m ago`;
    }

    if (ms < DAY_MS) {
        return `${Math.round(ms / HOUR_MS)}h ago`;
    }

    return `${Math.round(ms / DAY_MS)}d ago`;
};

export const buildDetailRows = (restriction: AirspaceRestriction): DetailRow[] => {
    const rows: DetailRow[] = [];

    const altitude = formatAltitudeBand(restriction);

    if (altitude !== null) {
        rows.push({ label: 'Altitude', value: altitude });
    }

    if (restriction.layer === LAANC_GRID_LAYER) {
        rows.push({ label: 'Authorization', value: 'Request via LAANC' });
    } else if (restriction.requiredAuthorizations.length > 0) {
        rows.push({ label: 'Authorization', value: restriction.requiredAuthorizations.join(', ') });
    }

    const effective = formatEffective(restriction);

    if (effective !== null) {
        rows.push({ label: 'Effective', value: effective });
    }

    if (restriction.distanceM !== null && restriction.distanceM > 0) {
        rows.push({
            label: 'Distance',
            value: `${Math.round(restriction.distanceM)} m from point`
        });
    }

    const updated = formatRelativeTime(restriction.dataLastSyncedAt);

    if (updated !== null) {
        rows.push({ label: 'Updated', value: updated });
    }

    return rows;
};

export default buildDetailRows;
